import { useQuery } from 'hooks/useCustomQuery';
import { useQueryClient } from '@tanstack/react-query';
import getPlatformNumberById from '@/API/PlatformNumber/getPlatformNumberById';
import getService from '@/API/Service/getService';

const useGetAvailableDRItems = (
    numberId,
    setVoicePolicies,
    setDialPlans,
    setIsDrNumber,
    initialNumber,
    platformId,
    accountScope,
    currentCompany,
) => {
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: [
            'useGetAvailableDRItems',
            numberId,
            platformId,
            accountScope,
        ],
        queryFn: async () => {
            try {
                if (numberId !== undefined) {
                    const res = await getPlatformNumberById(
                        numberId,
                        platformId,
                        accountScope,
                    );
                    const isGCCH =
                        res.trunkType === 'MS Direct Routing GCC High';
                    const newNumberIsDR =
                        res.trunkType === 'MS Direct Routing' || isGCCH;

                    setIsDrNumber(newNumberIsDR || false);
                    if (newNumberIsDR) {
                        let serviceData;

                        // For GCCH, get service from cached companyServices.
                        // useCustomQuery appends nav context to every key, so use
                        // getQueriesData (partial match) instead of getQueryData (exact).
                        if (isGCCH && currentCompany) {
                            const queriesData = queryClient.getQueriesData([
                                'services',
                                `company: ${currentCompany}`,
                            ]);
                            const cachedServices = queriesData?.[0]?.[1];
                            if (cachedServices) {
                                serviceData = cachedServices.find(
                                    (s) =>
                                        s.serviceType === 'DirectRoutingGCCH',
                                );
                            }
                        }

                        const serviceIdToUse = serviceData?.id || res.serviceID;
                        const serviceRes = await getService(serviceIdToUse);

                        setVoicePolicies(serviceRes?.data?.voicePolicies ?? []);
                        setDialPlans(serviceRes?.data?.dialPlans ?? []);
                    }
                    return res;
                } else {
                    setIsDrNumber(false);
                    return null;
                }
            } catch (error) {
                console.error(
                    'Error fetching available voice policies:',
                    error,
                );
                throw error;
            }
        },
        refetchOnWindowFocus: false,
        staleTime: 0,
        enabled: !!initialNumber,
    });
};

export default useGetAvailableDRItems;
